import React, { useRef, useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import FilterRuleRow, { CustomDropdown, getFieldIcon } from './FilterRuleRow';
import { useFilterState } from '../../hooks/useFilterState';
import { generateId, createEmptyTree } from '../../utils/filters/treeUtils';
import { OPERATOR_MAP, OPERATOR_LABELS, VALUE_REQUIRED_OPERATORS } from '../../utils/filters/filterConstants';
import SelectOptionPicker from './inputs/SelectOptionPicker';
import PersonPicker from './inputs/PersonPicker';
// RelativeDatePicker functionality is now integrated into GridDateValuePicker
import RelationPicker from './inputs/RelationPicker';
import GridDateValuePicker from './inputs/GridDateValuePicker';

const ICON_MAP = {
    'text': 'description', 'number': 'numbers', 'currency': 'payments', 'percentage': 'percent',
    'select': 'format_list_bulleted', 'multi_select': 'sell', 'status': 'radio_button_checked',
    'date': 'calendar_today', 'date_range': 'date_range', 'created_at': 'history', 'updated_at': 'update',
    'person': 'person', 'multi_person': 'groups', 'created_by': 'person_add',
    'checkbox': 'check_box', 'email': 'mail', 'phone': 'phone', 'url': 'link',
    'relation': 'sync_alt', 'formula': 'functions'
};

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 4, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 3, scale: 0.98 },
};
const PANEL_TRANSITION = { 
    duration: 0.15,
    ease: [0.16, 1, 0.3, 1]
};
const REDUCED_TRANSITION = { duration: 0.01 };

const FilterPanel = ({ schema = [], tree, onChange, onClose }) => {
    const safeTree = useMemo(() => {
        return tree && tree.children ? tree : createEmptyTree();
    }, [tree]);

    const { 
        tree: currentTree, addRule, addGroup, updateRule, removeNode, clearAll, setTree, turnIntoGroup
    } = useFilterState({ initialTree: safeTree, schema, onChange });

    const [searchQuery, setSearchQuery] = useState('');
    const [isAdvancedMode, setIsAdvancedMode] = useState(false);
    const [addRuleMenuOpen, setAddRuleMenuOpen] = useState(false);
    const addRuleRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (addRuleRef.current && !addRuleRef.current.contains(e.target)) setAddRuleMenuOpen(false);
        };
        if (addRuleMenuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addRuleMenuOpen]);

    const filteredSchema = useMemo(() => {
        if (!searchQuery) return schema.filter(s => !s.hidden);
        return schema.filter(s => !s.hidden && s.label.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [schema, searchQuery]);

    const safeCurrentTree = currentTree && currentTree.children ? currentTree : createEmptyTree();
    const allChildren = safeCurrentTree.children || [];
    const rules = allChildren.filter(c => c.type === 'rule');
    const groups = allChildren.filter(c => c.type === 'group' || c.type === 'compound');
    const hasContent = rules.length > 0 || groups.length > 0;

    const effectiveAdvanced = isAdvancedMode || rules.length > 1 || groups.length > 0;

    const getDefaultOp = (fieldType) => ({
        text: 'contains', number: 'equals', select: 'is', multi_select: 'contains',
        date: 'date_is', date_range: 'date_is', person: 'contains', checkbox: 'is_checked',
        status: 'is', email: 'contains', phone: 'contains', url: 'contains',
        currency: 'equals', percentage: 'equals', created_at: 'date_is', updated_at: 'date_is',
        created_by: 'contains', multi_person: 'contains', relation: 'contains'
    }[fieldType] || 'is');

    const handleAddRuleWithProperty = (propertyId) => {
        const field = schema.find(s => s.id === propertyId);
        if (!field) return;
        const newRule = {
            type: 'rule', id: generateId(), fieldId: field.id, propertyId: field.id,
            fieldType: field.type, operator: getDefaultOp(field.type), value: ''
        };
        setTree(prev => {
            const root = prev && prev.children ? prev : createEmptyTree();
            return { ...root, children: [...(root.children || []), newRule] };
        });
        setAddRuleMenuOpen(false);
        setSearchQuery('');
    };

    const handleAddEmptyRule = () => {
        const firstField = schema.find(s => !s.hidden);
        if (firstField) handleAddRuleWithProperty(firstField.id);
    };

    const handleAddGroup = () => {
        addGroup(safeCurrentTree.id, 'AND');
        setAddRuleMenuOpen(false);
    };

    const handleDuplicateRule = (rule) => {
        setTree(prev => {
            const root = prev && prev.children ? prev : createEmptyTree();
            return { ...root, children: [...(root.children || []), { ...rule, id: generateId() }] };
        });
    };

    const handleFieldChange = (ruleId, newFieldId) => {
        const newField = schema.find(s => s.id === newFieldId);
        if (!newField) return;
        updateRule(ruleId, { fieldId: newFieldId, propertyId: newFieldId, fieldType: newField.type, operator: getDefaultOp(newField.type), value: '' });
    };

    const switchToAdvanced = () => {
        if (!hasContent) handleAddEmptyRule();
        setIsAdvancedMode(true);
    };

    const prefersReducedMotion = useReducedMotion();

    return (
        <motion.div
            layout
            variants={PANEL_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={prefersReducedMotion ? REDUCED_TRANSITION : PANEL_TRANSITION}
            className="no-focus-glow absolute top-[calc(100%+6px)] right-0 z-[1000] overflow-visible flex flex-col bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.06)] box-border"
            style={{
                width: !hasContent ? '250px' : (effectiveAdvanced ? 'auto' : '360px'),
                minWidth: !hasContent ? '250px' : (effectiveAdvanced ? 'min(500px, calc(100vw - 32px))' : '360px'),
                maxWidth: 'calc(100vw - 32px)'
            }}
        >
            <AnimatePresence mode="wait" initial={false}>
                {!hasContent ? (
                    /* ── Empty State: Property Picker ── */
                    <motion.div 
                        key="empty-picker"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.12 }}
                        className="flex flex-col bg-[var(--bg-surface)] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[var(--border-subtle)] min-w-[220px] p-[6px]"
                    >
                        {/* Search Input */}
                        <div className="mb-1">
                            <input 
                                type="text"
                                placeholder="Filter by..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-[var(--bg-surface)] outline-none text-[13px] text-[var(--text-primary)] border border-[var(--border)] rounded-[6px] focus:border-[#2ea1ed] focus:ring-1 focus:ring-[#2ea1ed] transition-all placeholder:text-[var(--text-tertiary)] px-2 py-1"
                                autoFocus
                            />
                        </div>
                        
                        {/* List Items */}
                        <div className="max-h-[280px] overflow-y-auto custom-scrollbar flex flex-col gap-[2px]">
                            {filteredSchema.map(field => (
                                <div key={field.id} onClick={() => handleAddRuleWithProperty(field.id)}
                                    className="cursor-pointer text-[13px] text-[var(--text-primary)] rounded-[6px] flex items-center transition-colors duration-150 hover:bg-[var(--bg-muted)] py-[6px] px-2 gap-2"
                                >
                                    <div className="flex items-center justify-center shrink-0 w-4 h-4">
                                        <ObliqueIcon name={getFieldIcon(field)} size={16} color="var(--text-secondary)" />
                                    </div>
                                    <span>{field.label}</span>
                                </div>
                            ))}
                        </div>
                        
                        {/* Divider & Advanced */}
                        {filteredSchema.length > 0 && <div className="bg-[color:var(--border-subtle)] h-px my-1" />}
                        <div>
                            <div onClick={switchToAdvanced} className="cursor-pointer text-[13px] text-[var(--text-secondary)] rounded-[6px] flex items-center transition-colors duration-150 hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] py-[6px] px-2 gap-2">
                                <div className="flex items-center justify-center shrink-0 w-4 h-4">
                                    <ObliqueIcon name="add" size={16} color="var(--text-tertiary)" />
                                </div>
                                <span>Add advanced filter</span>
                            </div>
                        </div>
                    </motion.div>

                ) : effectiveAdvanced ? (
                    /* ── Advanced Mode ── */
                    <motion.div
                        key="advanced-mode"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.12 }}
                    >
                        <AdvancedFilterView
                            allChildren={allChildren} safeCurrentTree={safeCurrentTree} schema={schema}
                            updateRule={updateRule} removeNode={removeNode} handleDuplicateRule={handleDuplicateRule}
                            setTree={setTree} clearAll={clearAll} addRuleMenuOpen={addRuleMenuOpen}
                            setAddRuleMenuOpen={setAddRuleMenuOpen} addRuleRef={addRuleRef}
                            handleAddEmptyRule={handleAddEmptyRule} handleAddGroup={handleAddGroup}
                            handleFieldChange={handleFieldChange} addGroup={addGroup} addRule={addRule}
                            turnIntoGroup={turnIntoGroup}
                        />
                    </motion.div>

                ) : (
                    /* ── Preset Mode ── */
                    <motion.div
                        key="preset-mode"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.12 }}
                    >
                        <PresetFilterCard
                            rule={rules[0]} schema={schema}
                            onUpdate={(updates) => updateRule(rules[0].id, updates)}
                            onFieldChange={(newFieldId) => handleFieldChange(rules[0].id, newFieldId)}
                            onDelete={() => removeNode(rules[0].id)}
                            onSwitchAdvanced={() => setIsAdvancedMode(true)}
                            onAddFilter={handleAddEmptyRule}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};


/* ── Advanced Filter View ── */
const AdvancedFilterView = ({
    allChildren, safeCurrentTree, schema, updateRule, removeNode, handleDuplicateRule, setTree,
    clearAll, addRuleMenuOpen, setAddRuleMenuOpen, addRuleRef,
    handleAddEmptyRule, handleAddGroup, handleFieldChange, addGroup, addRule, turnIntoGroup
}) => {
    let childIndex = 0;
    return (
        <div className="flex flex-col">
            <div className="p-[10px_12px_4px] flex flex-col">
                <AnimatePresence initial={false}>
                    {allChildren.map((child) => {
                        const idx = childIndex++;
                        if (child.type === 'rule') {
                            return (
                                <FilterRuleRow
                                    key={child.id} rule={child} schema={schema} index={idx}
                                    groupOp={safeCurrentTree.operator}
                                    onUpdate={(updates) => updateRule(child.id, updates)}
                                    onDelete={() => removeNode(child.id)}
                                    onDuplicate={() => handleDuplicateRule(child)}
                                    onTurnIntoGroup={() => turnIntoGroup(child.id)}
                                    onChangeGroupOp={(op) => updateRule(safeCurrentTree.id, { operator: op })}
                                />
                            );
                        }
                        if (child.type === 'group' || child.type === 'compound') {
                            return (
                                <FilterGroupRow
                                    key={child.id} group={child} schema={schema} index={idx}
                                    parentOp={safeCurrentTree.operator}
                                    onChangeParentOp={(op) => updateRule(safeCurrentTree.id, { operator: op })}
                                    updateRule={updateRule} removeNode={removeNode}
                                    setTree={setTree} safeCurrentTree={safeCurrentTree}
                                    handleDuplicateRule={handleDuplicateRule} addGroup={addGroup} addRule={addRule}
                                    turnIntoGroup={turnIntoGroup}
                                />
                            );
                        }
                        return null;
                    })}
                </AnimatePresence>
            </div>

            {/* + Add filter rule ▾ */}
            <div className="p-[4px_12px_8px] relative" ref={addRuleRef}>
                <div onClick={() => setAddRuleMenuOpen(!addRuleMenuOpen)}
                    className="inline-flex items-center gap-[5px] text-[var(--text-tertiary)] text-[13px] cursor-pointer px-1.5 py-1 rounded-md select-none hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
                >
                    <ObliqueIcon name="add" size={14} />
                    <span>Add filter rule</span>
                    <ObliqueIcon name="keyboard_arrow_down" size={13} />
                </div>
                <AnimatePresence>
                    {addRuleMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                            transition={{ duration: 0.12 }}
                            className="absolute top-[100%] left-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.12)] z-[5000] w-[240px] p-1"
                        >
                            <div onClick={() => { handleAddEmptyRule(); setAddRuleMenuOpen(false); }}
                                className="flex items-center gap-2.5 px-2.5 py-2 cursor-pointer text-[13px] text-[var(--text-primary)] rounded-md hover:bg-[var(--bg-muted)]"
                            >
                                <ObliqueIcon name="add" size={16} color="var(--text-tertiary)" />
                                <span className="font-medium">Add filter rule</span>
                            </div>
                            <div onClick={() => { handleAddGroup(); setAddRuleMenuOpen(false); }}
                                className="flex items-start gap-2.5 px-2.5 py-2 cursor-pointer text-[13px] text-[var(--text-primary)] rounded-md hover:bg-[var(--bg-muted)]"
                            >
                                <div className="mt-[1px]">
                                    <ObliqueIcon name="library_add" size={16} color="var(--text-tertiary)" />
                                </div>
                                <div>
                                    <div className="font-medium">Add filter group</div>
                                    <div className="text-[11px] text-[var(--text-tertiary)] mt-[1px]">A group to nest more filters</div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Remove filter — always red */}
            <div className="border-t border-[var(--border-subtle)]" />
            <div className="p-[6px_12px_8px]">
                <div onClick={clearAll} className="inline-flex items-center gap-1.5 text-[#d4555e] text-[13px] cursor-pointer px-1.5 py-1 rounded-md select-none hover:bg-[color:var(--bg-subtle)]">
                    <ObliqueIcon name="delete" size={15} color="#d4555e" />
                    <span>Remove filter</span>
                </div>
            </div>
        </div>
    );
};


/* ── Filter Group Row — Nested group with its own rules ── */
const FilterGroupRow = ({ group, schema, index, parentOp, onChangeParentOp, updateRule, removeNode, setTree, safeCurrentTree, handleDuplicateRule, addGroup, addRule, turnIntoGroup }) => {
    const groupChildren = group.children || [];
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const addRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (addRef.current && !addRef.current.contains(e.target)) setAddMenuOpen(false);
        };
        if (addMenuOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addMenuOpen]);

    const addRuleToGroup = () => {
        // Use addRule from the hook — it uses setTree(prev => ...) to avoid stale closures
        addRule(group.id);
        setAddMenuOpen(false);
    };

    const updateGroupOp = (op) => {
        // Use updateRule which also uses setTree(prev => ...) via updateNode
        updateRule(group.id, { operator: op });
    };

    return (
        <div className="py-1">
            {/* And/Or label for the group */}
            <div className="flex items-start gap-2">
                <div className="mt-[9px] shrink-0">
                    {index === 0 ? (
                        <div className="h-7 flex items-center text-[13px] font-normal text-[var(--text-secondary)] min-w-[52px] pl-2.5 shrink-0 box-border">Where</div>
                    ) : index === 1 ? (
                        <CustomDropdown
                            value={parentOp}
                            options={[{ value: 'AND', label: 'And' }, { value: 'OR', label: 'Or' }]}
                            onChange={onChangeParentOp}
                            minWidth="52px"
                            className="shrink-0"
                        />
                    ) : (
                        <div className="h-7 flex items-center text-[13px] font-normal text-[var(--text-secondary)] min-w-[52px] pl-2.5 shrink-0 box-border">
                            {parentOp === 'OR' ? 'Or' : 'And'}
                        </div>
                    )}
                </div>

                {/* Group box */}
                <div className="flex-1 bg-[color:var(--bg-subtle)] rounded-lg border border-[var(--border)] pt-2.5 pb-2 px-3">
                    <AnimatePresence initial={false}>
                        {groupChildren.map((child, idx) => {
                            if (child.type === 'rule') {
                                return (
                                    <FilterRuleRow
                                        key={child.id} rule={child} schema={schema} index={idx}
                                        groupOp={group.operator}
                                        onUpdate={(updates) => updateRule(child.id, updates)}
                                        onDelete={() => removeNode(child.id)}
                                        onDuplicate={() => handleDuplicateRule(child)}
                                        onTurnIntoGroup={() => turnIntoGroup(child.id)}
                                        onChangeGroupOp={updateGroupOp}
                                    />
                                );
                            }
                            if (child.type === 'group' || child.type === 'compound') {
                                return (
                                    <FilterGroupRow
                                        key={child.id} group={child} schema={schema} index={idx}
                                        parentOp={group.operator}
                                        onChangeParentOp={updateGroupOp}
                                        updateRule={updateRule} removeNode={removeNode}
                                        setTree={setTree} safeCurrentTree={safeCurrentTree}
                                        handleDuplicateRule={handleDuplicateRule} addGroup={addGroup} addRule={addRule}
                                        turnIntoGroup={turnIntoGroup}
                                    />
                                );
                            }
                            return null;
                        })}
                    </AnimatePresence>

                    {/* Add rule inside group */}
                    <div className="py-0.5 relative" ref={addRef}>
                        <div onClick={() => setAddMenuOpen(!addMenuOpen)}
                            className="inline-flex items-center gap-[5px] text-[var(--text-tertiary)] text-xs cursor-pointer px-1.5 py-1 rounded-md select-none hover:bg-[var(--bg-muted)] hover:text-[var(--text-secondary)]"
                        >
                            <ObliqueIcon name="add" size={13} />
                            <span>Add filter rule</span>
                            <ObliqueIcon name="keyboard_arrow_down" size={12} />
                        </div>
                        <AnimatePresence>
                            {addMenuOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                                    transition={{ duration: 0.12 }}
                                    className="absolute top-[100%] left-0 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.12)] z-[5000] w-[200px] p-1"
                                >
                                    <div onClick={addRuleToGroup}
                                        className="flex items-center gap-2 px-2.5 py-[7px] cursor-pointer text-[13px] text-[var(--text-primary)] rounded-md hover:bg-[var(--bg-muted)]"
                                    >
                                        <ObliqueIcon name="add" size={15} color="var(--text-tertiary)" />
                                        <span>Add filter rule</span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* ··· for group */}
                <div className="pt-1 shrink-0">
                    <button onClick={() => removeNode(group.id)}
                        className="w-[26px] h-[26px] rounded-md bg-transparent border-none text-[var(--text-tertiary)] flex items-center justify-center cursor-pointer hover:bg-[var(--bg-muted)]"
                    >
                        <ObliqueIcon name="close" size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};


/* ── Preset Filter Card ── */
const PresetFilterCard = ({ rule, schema, onUpdate, onFieldChange, onDelete, onSwitchAdvanced, onAddFilter }) => {
    const field = schema.find(s => s.id === rule.fieldId);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const optionsRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target)) setOptionsOpen(false);
        };
        if (optionsOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [optionsOpen]);

    const validOperators = OPERATOR_MAP[rule.fieldType] || ['is'];
    const showValueInput = VALUE_REQUIRED_OPERATORS.has(rule.operator);

    const handleOperatorChange = (newOp) => {
        if (!VALUE_REQUIRED_OPERATORS.has(newOp)) onUpdate({ operator: newOp, value: undefined });
        else onUpdate({ operator: newOp });
    };

    const fieldOptions = schema.filter(s => !s.hidden).map(s => ({
        value: s.id, label: s.label, icon: getFieldIcon(s)
    }));

    const operatorOptions = validOperators.map(op => ({
        value: op, label: OPERATOR_LABELS[op] || op
    }));

    const renderValueInput = () => {
        if (!showValueInput) return null;

        const baseInputClass = "h-7 px-3 border border-[var(--border)] rounded-md text-[13px] w-full bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none shadow-none transition-all duration-150 focus:border-[color:var(--border-strong)]";

        switch (rule.fieldType) {
            case 'text': case 'email': case 'phone': case 'url':
                return <input type="text" placeholder="Type a value..." value={rule.value || ''} onChange={e => onUpdate({ value: e.target.value })} className={baseInputClass} />;
            case 'number': case 'currency': case 'percentage':
                return <input type="number" placeholder="Value" value={rule.value || ''} onChange={e => onUpdate({ value: parseFloat(e.target.value) })} className={baseInputClass} />;
            case 'select': case 'status':
                return <SelectOptionPicker field={field} single value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'multi_select':
                return <SelectOptionPicker field={field} multi value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'person': case 'created_by':
                return <PersonPicker single value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'multi_person':
                return <PersonPicker multi value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'relation':
                return <RelationPicker moduleId={field?.relatedModuleId} value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'date': case 'created_at': case 'updated_at': case 'date_range':
                return <GridDateValuePicker operator={rule.operator} value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            default: return null;
        }
    };

    return (
        <div className="flex flex-col no-focus-glow">
            {/* Header row: Property pill (dropdown) + operator + ··· with proper spacing */}
            <div className="pt-2.5 pb-2 px-3 flex items-center gap-2 w-full box-border">
                
                {/* Field Label (Static, not a dropdown) */}
                <div className="flex items-center text-[13px] text-[var(--text-secondary)] font-medium shrink-0 whitespace-nowrap overflow-hidden text-ellipsis max-w-[140px]" title={field?.label || 'Field'}>
                    {field?.label || 'Field'}
                </div>

                {/* Operator Selector */}
                <div className="shrink-0 flex-1 min-w-[100px]">
                    <CustomDropdown
                        value={rule.operator}
                        options={operatorOptions}
                        onChange={handleOperatorChange}
                    />
                </div>

                {/* ··· menu - strictly pushed to the right corner */}
                <div ref={optionsRef} className="relative shrink-0 ml-1">
                    <button onClick={() => setOptionsOpen(!optionsOpen)}
                        className="w-[26px] h-[26px] rounded-md bg-transparent border-none text-[var(--text-tertiary)] flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)]"
                    >
                        <ObliqueIcon name="more_horiz" size={18} />
                    </button>
                    <AnimatePresence>
                        {optionsOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: 0.12 }}
                                className="absolute right-0 top-[calc(100%+4px)] bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.12)] z-[5000] min-w-[200px] p-1"
                            >
                                <div onClick={() => { onDelete(); setOptionsOpen(false); }}
                                    className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-[#d4555e] cursor-pointer rounded-md transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                >
                                    <ObliqueIcon name="delete" size={15} color="#d4555e" />
                                    Remove filter
                                </div>
                                <div onClick={() => { onSwitchAdvanced(); setOptionsOpen(false); }}
                                    className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-[var(--text-primary)] cursor-pointer rounded-md transition-colors duration-150 hover:bg-[var(--bg-muted)]"
                                >
                                    <ObliqueIcon name="tune" size={15} color="var(--text-secondary)" />
                                    Add to advanced filter
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Value input */}
            {showValueInput && (
                <div className="px-3 pb-3">
                    {renderValueInput()}
                </div>
            )}
        </div>
    );
};

export default FilterPanel;
